import { BehaviorSubject, filter, map, Observable, of, shareReplay, Subject, takeUntil } from "rxjs";

/**
 * libs
 */
import { v6 as UUID_V6 } from 'uuid'
import mqtt, { DoneCallback, ErrorWithReasonCode, IClientOptions, IDisconnectPacket, MqttClient, Packet } from "mqtt";

/**
 * interfaces
*/
import { ConnectionState, IConnectionResult, IConnectionMessage, IMqttAuthProviderSnapshot } from "../../interfaces";

/**
 * utilities
 */
import { JwtUtils } from "@core/utilities";

/**
 * Wraps the `mqtt.js` Client to provide a robust, reactive connection handler.
 * @class ConnectionHandler
 * @description Manages the lifecycle of a single MQTT connection.
 * It handles:
 * - Connection initialization and options configuration.
 * - Dynamic authentication via `transformWsUrl`.
 * - Event bridging to RxJS Observables (`state$`, `message$`).
 * - Automatic reconnection logic coordinated with the Auth Provider.
 */
export class ConnectionHandler {

    private readonly _destroy$ = new Subject<void>();

    /**
     * Unique suffix of this connection's client id.
     *
     * @remarks
     * Held in memory, generated once per handler — which *is* the per-connection
     * identity, since one handler owns one socket. Two consequences, both
     * intended:
     *
     * - It cannot collide. A client id derived from the token alone would be
     *   identical in every tab of the same user, and a broker resolves a
     *   duplicate client id by evicting the older session — one tab silently
     *   kicking another off the bus.
     * - It is not persisted. `sessionStorage` would survive a reload, but the
     *   browser *copies* it when a tab is duplicated, re-creating the very
     *   collision above. A reload simply takes a new id here, and the session it
     *   left behind dies with the socket.
     *
     * @private
     */
    private readonly _clientUuid: string = UUID_V6();

    /**
     * Subject to track the current connection state.
     * @private
     */
    private readonly _connectResultSubject$: BehaviorSubject<IConnectionResult> = new BehaviorSubject<IConnectionResult>({ state: 'initializing' });
    
    /**
     * Observable stream of connection states (e.g., connected, disconnected, error).
     * @public
     */
    private readonly connectResult$: Observable<IConnectionResult> = this._connectResultSubject$.asObservable();

    /**
     * Updates the connection result subject.
     * @private
     * @param {ConnectionState} state - The new state.
     * @param {Packet} [packet] - The packet associated with the state change (if any).
     * @param {Error | ErrorWithReasonCode | null} [err] - The error associated with the state change (if any).
     */
    private _setConnectionResult(state: ConnectionState, packet?: Packet, err?: Error | ErrorWithReasonCode | null): void {
        this._connectResultSubject$.next({ state, packet, err });
    }

    /**
     * Subject to stream incoming MQTT messages.
     * @private
     */
    private readonly _messageSubject$: BehaviorSubject<IConnectionMessage | null> = new BehaviorSubject<IConnectionMessage | null>(null);
    
    /**
     * Hot Observable stream of all incoming MQTT messages for this connection.
     * Filters out null values and shares the latest message via `shareReplay`.
     * @public
     */
    public readonly message$: Observable<IConnectionMessage> = this._messageSubject$.asObservable().pipe(
        filter((message) => message !== null),
        shareReplay({ refCount: true }),
    );

    /**
     * Flag to track if a successful connection has been made at least once.
     * Used to distinguish between initial `connect()` and subsequent `reconnect()` calls.
     * @private
     */
    private _connectedAtLeastOnce: boolean = false;

    /**
     * Getter for the connected-at-least-once flag.
     * @public
     */
    public get connectedAtLeastOnce(): boolean {
        return this._connectedAtLeastOnce;
    }

    /**
     * The underlying `mqtt.js` MqttClient instance.
     * @private
     */
    private _mqttClient!: MqttClient;

    /**
     * Getter for the underlying MqttClient.
     * @public
     */
    public get mqttClient(): MqttClient {
        return this._mqttClient;
    }

    /**
     * Checks if the client is currently connected.
     * @public
     */
    public get connected(): boolean {
        return this._mqttClient.connected;
    }

    /**
     * Checks if the client is currently disconnected.
     * @public
     */
    public get disconnected(): boolean {
        return this._mqttClient.disconnected;
    }

    /**
     * Checks if the client is in the process of disconnecting.
     * @public
     */
    public get disconnecting(): boolean {
        return this._mqttClient.disconnecting;
    }

    /**
     * Checks if the client is in the process of reconnecting.
     * @public
     */
    public get reconnecting(): boolean {
        return this._mqttClient.reconnecting;
    }
    

    /**
     * Creates an instance of ConnectionHandler.
     * @constructor
     * @param {string} url - The WebSocket URL for the broker.
     * @param {string} connectionId - A unique identifier for this connection.
     * @param {IClientOptions} options - MQTT connection options.
     * @param {IMqttAuthProviderSnapshot} [authProvider] - Optional provider for dynamic auth tokens.
     * @throws {Error} If URL or ConnectionID are invalid.
     */
    constructor(
        public readonly url: string,
        public readonly connectionId: string,
        public readonly options: IClientOptions,
        public readonly authProvider?: IMqttAuthProviderSnapshot
    ) {

        // global validation
        if (typeof this.url !== 'string' || this.url.length === 0) {
            throw new Error('URL must be a string & non-empty');
        }
        if (typeof this.connectionId !== 'string' || this.connectionId.length === 0) {
            throw new Error('Connection ID must be a string & non-empty');
        }
        if (typeof this.options !== 'object') {
            throw new Error('Options must be an object');
        }

        // apply the auth config
        const _options = this._authConfig(this.options);

        // initialize the mqtt client
        this._initializeMqttClient(_options);

        // subscribe to the auth provider observable
        // if the auth provider is ready, connect to the MQTT broker
        // if the auth provider is not ready, reconnect to the MQTT broker
        this._authProviderObservable$
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (state: boolean) => {

                    // if the auth provider is not ready, do nothing
                    if (!state) {
                        return;
                    }
                    
                    // if the connection has not been established at least once, connect to the MQTT broker
                    if (!this.connectedAtLeastOnce) {
                        this.connect();
                    }
                    else {
                        this.reconnect();
                    }
                    
                },
        })

    }

    /**
     * Configures the MQTT options, specifically the `transformWsUrl` hook for dynamic auth.
     * @private
     * @param {IClientOptions} options - The base options.
     * @returns {IClientOptions} The options with the transform hook attached.
     */
    private _authConfig(options: IClientOptions): IClientOptions {

        // Fill the credentials in once up front, so the very first CONNECT
        // packet is correct by construction rather than as a side effect of the
        // transform hook below.
        return this._applyAuthOptions({
            ...options,
            transformWsUrl: (url, opts) => {

                // Runs immediately before every connect and reconnect attempt —
                // the one point where a token refreshed since the last attempt
                // can still reach the CONNECT packet.
                this._applyAuthOptions(opts);

                return url;
            },
        });

    }

    /**
     * Writes the broker credentials and the Last Will onto a set of client
     * options, reading the current JWT each time it is called.
     *
     * @remarks
     * The shape is fixed by the broker contract agreed on 2026-02-02, and each
     * field carries a different guarantee:
     *
     * - `username` — `WEBuser_{user_id}`, deliberately **not** unique. It is the
     *   identity the broker's ACL is written against, so every session of one
     *   user must present the same one.
     * - `password` — the user's JWT. A missing or expired token is refused at
     *   CONNECT, which is what keeps an unauthenticated tab off the bus.
     * - `clientId` — `WEBuser_{user_id}_{uuid}`, unique per connection via
     *   {@link _clientUuid}. See that field for why uniqueness is load-bearing.
     * - `will` — published by the *broker*, not by us, if this socket ever dies
     *   without a DISCONNECT packet (tab closed, laptop slept, network dropped).
     *   It is the only signal the backend gets for a user who leaves without
     *   logging out.
     *
     * Mutates `options` in place rather than returning a copy, because
     * `transformWsUrl` hands us the live options object that `mqtt.js` is about
     * to build its CONNECT packet from — a copy would be discarded.
     *
     * @remarks
     * Depends on broker-side grants, and a CONNACK `0x87` (Not authorized) is
     * the symptom when one is missing: the broker must know the new
     * username/client-id scheme, and this connection must be allowed to publish
     * to `v1/ext/user/status/<user_id>` — MQTT 5 refuses the whole CONNECT for
     * an unauthorised will topic rather than merely dropping the will.
     *
     * @private
     * @param {IClientOptions} options - Client options to write the credentials into.
     * @returns {IClientOptions} The same object, for call-site convenience.
     */
    private _applyAuthOptions(options: IClientOptions): IClientOptions {

        const accessToken = this.authProvider?.accessToken ?? '';
        const { sub: userId } = new JwtUtils(accessToken).payload() ?? {};

        options.username = `WEBuser_${userId}`;
        options.password = accessToken;
        options.clientId = `WEBuser_${userId}_${this._clientUuid}`;

        options.will = {
            topic: `v1/ext/user/status/${userId}`,
            payload: JSON.stringify({
                status: false,
                reasons: 'UnExpected disconnection',
            }),
            qos: 0,
            retain: false,
        };

        return options;

    }

    /**
     * Initializes the underlying MqttClient instance.
     * @private
     * @param {IClientOptions} options - The configured options.
     */
    private _initializeMqttClient(options: IClientOptions): void {
    
        // global validation
        if (typeof options !== 'object') {
            throw new Error('Options must be an object');
        }

        // log the initialization
        console.log('initializing mqtt client');

        // set the connection state to initializing
        this._setConnectionResult('initializing');

        // initialize the MQTT client
        this._mqttClient = mqtt.connect(this.url, options);

        // add the event handlers
        this._eventHandlers();
    }

    /**
     * Initiates the connection process.
     * If already connected, logs a message and returns.
     * Sets `connectedAtLeastOnce` to true.
     * @public
     */
    public connect(): void {
        
        if (this.connected) {
            console.log('already connected');
            return;
        }

        // log the connection attempt
        console.log('connecting to mqtt broker');

        // set the connection state to connecting
        this._setConnectionResult('connecting');

        // set the connected at least once flag
        if (this._connectedAtLeastOnce === false) {
            this._connectedAtLeastOnce = true;
        }

        this._mqttClient.connect();

    }

    /**
     * Forces a reconnection attempt.
     * Useful when connection options (like tokens) have changed.
     * @public
     * @param {Pick<IClientOptions, 'incomingStore' | 'outgoingStore'>} [opts] - Optional store options.
     */
    public reconnect(opts?: Pick<IClientOptions, 'incomingStore' | 'outgoingStore'>): void {
        
        if (this.reconnecting) {
            console.log('already reconnecting');
            return;
        }

        // set the connection state to reconnecting
        this._setConnectionResult('reconnecting');

        this._mqttClient.reconnect(opts);
    }

    /**
     * Disconnects the client.
     * @public
     * @param {boolean} [force=false] - If true, closes immediately without waiting for in-flight packets.
     * @param {Partial<IDisconnectPacket>} [opts] - Disconnect packet options (MQTT 5.0).
     * @param {DoneCallback} [cb] - Callback when disconnect is complete.
     */
    public disconnect(force: boolean = false, opts?: Partial<IDisconnectPacket>, cb?: DoneCallback): void {
        
        // global validation
        if (this.disconnected) {
            console.log('already end');
            return;
        }
        if (this.disconnecting) {
            console.log('already end');
            return;
        }

        // set the connection state to end
        this._setConnectionResult('end');
        this._destroy$.next();
        this._destroy$.complete();

        this._mqttClient.end(force, opts, cb);
    }

    /**
     * Forcefully disconnects the client immediately.
     * @public
     */
    public forceDisconnect(): void {
        this.disconnect(true);
    }

    /**
     * Attaches all internal event listeners to the MqttClient.
     * Bridges MQTT events to the reactive `connectResult$` and `message$` streams.
     * @private
     */
    private _eventHandlers(): void {

        // connect event handler
        this._mqttClient.on('connect', (...args: any[]) => {
            this._setConnectionResult('connected');
        });

        // disconnect event handler
        this._mqttClient.on('disconnect', (...args: any[]) => {
            this._setConnectionResult('disconnected');
        });

        // error event handler
        this._mqttClient.on("error", (...args: any[]) => {
            this._setConnectionResult('error');
        });
    
        // close event handler
        this._mqttClient.on("close", (...args: any[]) => {
            this._setConnectionResult('end');
        });
    
        // reconnect event handler
        this._mqttClient.on("reconnect", (...args: any[]) => {
            this._setConnectionResult('reconnected');
        });
    
        // offline event handler
        this._mqttClient.on("offline", (...args: any[]) => {
            this._setConnectionResult('offline');
        });
    
        // end event handler
        this._mqttClient.on("end", (...args: any[]) => {
            this._setConnectionResult('end');
        });

        // end event handler
        this._mqttClient.on("message", (topic: string, message: Uint8Array, packet: Packet) => {
            // console.log('message', topic, message, packet);
            this._messageSubject$.next({ topic, payload: message, packet });
        });
    }
    
    /**
     * Removes all event listeners from the underlying client.
     * @public
     */
    public removeAllListeners(): void {
        this._mqttClient.removeAllListeners()
    }

    /**
     * Gets the auth provider observable.
     * Returns an observable that emits true when the auth token is ready (not refreshing).
     * @private
     */
    private get _authProviderObservable$(): Observable<boolean> {

        // if no auth provider, always ready immediately
        if (this.authProvider == null) {
            return of(false);
        }

        // if auth provider exists, wait for refreshing to be false
        return this.authProvider.refreshing$.pipe(
            filter(isRefreshing => !isRefreshing),
            map(() => true),
        );
    }

    /**
     * Cleans up resources, disconnects, and completes Observables.
     * @public
     */
    public destroy(): void {
        this.removeAllListeners();
        this.disconnect(true);
        this._messageSubject$.complete();
        this._connectResultSubject$.complete();
    }

    
}
